import type { Permission, PermissionGroup } from './api';

type PermissionInfo = PermissionGroup['permissions'][number];

/**
 * One line of the permission matrix: an area (e.g. `colonies`) with a read
 * and a write level. `members.manage` and `roles.manage` count as write
 * level; permissions without a read/write meaning get a line of their own.
 */
export interface PermissionRow {
  key: string;
  /** Whether the row is an area (label `permissions.areas.<key>`) or a single permission. */
  area: boolean;
  read?: PermissionInfo;
  write?: PermissionInfo;
}

export function permissionRows(group: PermissionGroup): PermissionRow[] {
  const rows: PermissionRow[] = [];
  const areaRow = (key: string) => {
    let row = rows.find((r) => r.area && r.key === key);
    if (!row) {
      row = { key, area: true };
      rows.push(row);
    }
    return row;
  };
  for (const p of group.permissions) {
    const [area = p.key, action] = p.key.split('.');
    if (action === 'read') areaRow(area).read = p;
    else if (action === 'write' || action === 'manage') areaRow(area).write = p;
    else rows.push({ key: p.key, area: false, write: p });
  }
  return rows;
}

function catalogMap(catalog: PermissionGroup[]): Map<Permission, PermissionInfo> {
  return new Map(catalog.flatMap((g) => g.permissions.map((p) => [p.key, p] as const)));
}

/** Grants `permission` together with everything it requires. */
export function grant(
  set: ReadonlySet<Permission>,
  permission: Permission,
  catalog: PermissionGroup[],
): Set<Permission> {
  const byKey = catalogMap(catalog);
  const result = new Set(set);
  const add = (p: Permission) => {
    if (result.has(p)) return;
    result.add(p);
    byKey.get(p)?.requires.forEach(add);
  };
  add(permission);
  return result;
}

/** Revokes `permission` and everything that requires it. */
export function revoke(
  set: ReadonlySet<Permission>,
  permission: Permission,
  catalog: PermissionGroup[],
): Set<Permission> {
  const byKey = catalogMap(catalog);
  const result = new Set(set);
  const remove = (p: Permission) => {
    if (!result.delete(p)) return;
    for (const other of result) {
      if (byKey.get(other)?.requires.includes(p)) remove(other);
    }
  };
  remove(permission);
  return result;
}

/** Whether a role with `permissions` may be handed out by someone who can do `can`. */
export function grantable(permissions: Permission[], can: (p: Permission) => boolean): boolean {
  return permissions.every(can);
}

/** Areas a role may edit and areas it may only read, in catalog order. */
export function accessSummary(
  permissions: Permission[],
  catalog: PermissionGroup[],
): { write: PermissionRow[]; read: PermissionRow[] } {
  const granted = new Set(permissions);
  const write: PermissionRow[] = [];
  const read: PermissionRow[] = [];
  for (const row of catalog.flatMap(permissionRows)) {
    if (row.write && granted.has(row.write.key)) write.push(row);
    else if (row.read && granted.has(row.read.key)) read.push(row);
  }
  return { write, read };
}
